/**
 * API 服务层
 * 处理所有与后端的 HTTP 通信
 */

import type {
  ApiResponse,
  PaginatedResponse,
  LoginRequest,
  TokenResponse,
  User,
  KnowledgeArchive,
  KnowledgeArchiveCreate,
  KnowledgeArchiveUpdate,
  ExamPaper,
  ExamPaperWithQuestions,
  ExamPaperCreate,
  ExamPaperUpdate,
  ExamPaperUpdateWithQuestions,
  LLMExamGenerate,
  ExamTask,
  ExamTaskCreate,
  ExamTaskUpdate,
  ExamSubmit,
  ExamResult,
  ExamSubmitResult,
  LearningTask,
  LearningTaskCreate,
  LearningTaskUpdate,
  UserCreate,
  UserUpdate,
  ChatRequest,
  ChatResponse,
  ChatSession,
  ChatHistory,
  ProcessStatus,
  RoleOut,
  RoleCreate,
  RoleUpdate,
  Permission,
  Tag,
  TagCreate,
  TagUpdate,
  Department,
  AssignedStudent,
  TaskAssignRequest,
  StudentExamTask,
  StudentLearningTask,
  LearningTaskDetail,
  LearningFileProgress,
  UpdateFileProgressRequest,
  ExamQuestion,
  QuestionCreateInPaper,
  QuestionUpdateInPaper,
  QuestionReorderItem,
  TaskStudentsCompletion,
  VisitRecord,
  VisitExtractRequest,
  VisitInfoExtracted,
  VisitRecordCreate,
  PromotionFile, PromotionFileCreate, PromotionFileUpdate, PromotionViewLog,
  QiBasicInfo, QiProductInfo, QiIncidentScene, QiDefectDetails,
  QiLosses, QiResponsibility, QiImprovement,
  QualityIncidentDetail, QualityIncidentListItem,
  DrawingComponentType, DrawingParamItem,
  DrawingMaterial, DrawingMaterialListItem, DrawingFileItem, DrawingProduct,
  DrawingProductListItem, DrawingDoc, DrawingDocTemplate, DrawingPreviewBlock,
  ExtractionResult, ExtractionConfirmRequest,
} from '@/types/api';

const API_BASE = '/api';

/**
 * 从未知错误中提取错误消息
 * 用于安全地处理 catch 块中的错误
 */
export function getErrorMessage(error: unknown): string {
  if (error instanceof Error) {
    return error.message;
  }
  if (typeof error === 'string') {
    return error;
  }
  return '操作失败，请稍后重试';
}

class ApiService {
  private token: string | null = null;

  constructor() {
    // 从 localStorage 恢复 token
    this.token = localStorage.getItem('access_token');
  }

  setToken(token: string | null) {
    this.token = token;
    if (token) {
      localStorage.setItem('access_token', token);
    } else {
      localStorage.removeItem('access_token');
      localStorage.removeItem('refresh_token');
    }
  }

  async request<T>(
    endpoint: string,
    options: RequestInit = {},
    skipAuthRedirect: boolean = false
  ): Promise<T> {
    const url = `${API_BASE}${endpoint}`;
    // 记录发请求时的令牌（多标签页场景：401 时若期间已有新登录，本请求作废而不拖累新会话）
    const tokenAtSend = this.token;

    // FormData（multipart）不能手动设置 Content-Type，由浏览器自动生成带 boundary 的头
    const headers: Record<string, string> = { ...(options.headers as Record<string, string>) };
    if (!(options.body instanceof FormData)) {
      headers['Content-Type'] = 'application/json';
    }

    if (this.token) {
      (headers as Record<string, string>)['Authorization'] = `Bearer ${this.token}`;
    }

    const response = await fetch(url, {
      ...options,
      headers,
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      let errorMessage: string = errorData.message || `请求失败: ${response.status}`;
      // 透传后端参数校验明细（details），避免只见笼统的“请求参数验证失败”
      if (Array.isArray(errorData.details) && errorData.details.length) {
        const detailText = errorData.details
          .map((d: any) => d?.message || '')
          .filter(Boolean)
          .slice(0, 3)
          .join('；');
        if (detailText) errorMessage += `（${detailText}）`;
      }

      if (response.status === 401 && !skipAuthRedirect) {
        // 多标签页保护：本请求持旧令牌、而期间别处已完成新登录（localStorage 已更新），
        // 说明这是残留标签页的过时请求——静默作废，不清令牌、不写踢出标记、不跳转，
        // 避免旧标签页把新登录的会话全局污染成"已在其他设备登录"
        const latestToken = typeof localStorage !== 'undefined' ? localStorage.getItem('access_token') : null;
        if (tokenAtSend && latestToken && latestToken !== tokenAtSend) {
          throw new Error(errorMessage);
        }
        // Token 过期或账号在其他设备登录，清除登录状态并跳转登录页
        this.setToken(null);
        if (errorMessage.includes('其他设备')) {
          localStorage.setItem('session_kicked', 'true');
        } else {
          localStorage.setItem('session_expired', 'true');
        }
        window.location.href = '/login';
      }

      throw new Error(errorMessage);
    }

    return response.json();
  }

  // ===== 认证相关 =====
  async login(data: LoginRequest): Promise<ApiResponse<TokenResponse>> {
    const response = await this.request<ApiResponse<TokenResponse>>(
      '/auth/login',
      {
        method: 'POST',
        body: JSON.stringify(data),
      },
      true // 登录请求跳过自动跳转，使用后端返回的错误消息
    );

    if (response.data) {
      this.setToken(response.data.access_token);
      localStorage.setItem('refresh_token', response.data.refresh_token);
    }

    return response;
  }

  async refreshToken(): Promise<ApiResponse<TokenResponse>> {
    const refreshToken = localStorage.getItem('refresh_token');
    if (!refreshToken) {
      throw new Error('No refresh token');
    }

    const response = await this.request<ApiResponse<TokenResponse>>('/auth/refresh', {
      method: 'POST',
      body: JSON.stringify({ refresh_token: refreshToken }),
    });

    if (response.data) {
      this.setToken(response.data.access_token);
      localStorage.setItem('refresh_token', response.data.refresh_token);
    }

    return response;
  }

  async sendCode(phone: string): Promise<ApiResponse<any>> {
    return this.request('/auth/send-code', { method: 'POST', body: JSON.stringify({ phone }) });
  }

  async logout() {
    try {
      await this.request('/auth/logout', { method: 'POST' });
    } catch {
      // 即使请求失败也继续执行登出流程
    }
    this.setToken(null);
    localStorage.removeItem('refresh_token');
    window.location.href = '/login';
  }

  // ===== 用户管理 =====
  async getUsers(params?: {
    page?: number;
    page_size?: number;
    role_id?: number;
    role_ids?: string;  // 多角色筛选，逗号分隔如 '2,3'
    exclude_admins?: boolean;  // 排除管理员和超级管理员
  }): Promise<PaginatedResponse<User>> {
    const searchParams = new URLSearchParams();
    if (params?.page) searchParams.set('page', String(params.page));
    if (params?.page_size) searchParams.set('page_size', String(params.page_size));
    if (params?.role_id) searchParams.set('role_id', String(params.role_id));
    if (params?.role_ids) searchParams.set('role_ids', params.role_ids);
    if (params?.exclude_admins) searchParams.set('exclude_admins', 'true');

    return this.request<PaginatedResponse<User>>(`/users/?${searchParams}`);
  }

  async getAssignableUsers(params?: {
    page?: number;
    page_size?: number;
  }): Promise<PaginatedResponse<User>> {
    const searchParams = new URLSearchParams();
    if (params?.page) searchParams.set('page', String(params.page));
    if (params?.page_size) searchParams.set('page_size', String(params.page_size));

    return this.request<PaginatedResponse<User>>(`/users/assignable?${searchParams}`);
  }

  async getUserRoles(): Promise<ApiResponse<{ role_id: number; role_name: string }[]>> {
    return this.request<ApiResponse<{ role_id: number; role_name: string }[]>>('/users/roles');
  }

  async createUser(data: UserCreate): Promise<ApiResponse<User>> {
    return this.request<ApiResponse<User>>('/users/', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateUser(account: string, data: UserUpdate): Promise<ApiResponse<User>> {
    const searchParams = new URLSearchParams();
    searchParams.set('account', account);

    return this.request<ApiResponse<User>>(`/users/?${searchParams}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async changePassword(oldPassword: string, newPassword: string): Promise<ApiResponse<void>> {
    return this.request<ApiResponse<void>>('/users/me/password', {
      method: 'PUT',
      body: JSON.stringify({
        old_password: oldPassword,
        new_password: newPassword,
      }),
    });
  }

  async resetPassword(account: string): Promise<ApiResponse<void>> {
    return this.request<ApiResponse<void>>('/users/reset-password', {
      method: 'POST',
      body: JSON.stringify({
        account: account,
      }),
    });
  }

  async deleteUser(account: string): Promise<ApiResponse<void>> {
    const searchParams = new URLSearchParams();
    searchParams.set('account', account);

    return this.request<ApiResponse<void>>(`/users/?${searchParams}`, {
      method: 'DELETE',
    });
  }

  async batchDeleteUsers(accounts: string[]): Promise<{ success: string[]; failed: { account: string; error: string }[] }> {
    const results = { success: [] as string[], failed: [] as { account: string; error: string }[] };

    for (const account of accounts) {
      try {
        await this.deleteUser(account);
        results.success.push(account);
      } catch (error) {
        results.failed.push({
          account,
          error: error instanceof Error ? error.message : '删除失败'
        });
      }
    }

    return results;
  }

  async toggleUserActive(account: string): Promise<ApiResponse<User>> {
    const searchParams = new URLSearchParams();
    searchParams.set('account', account);

    return this.request<ApiResponse<User>>(`/users/toggle-active?${searchParams}`, {
      method: 'POST',
    });
  }

  async getDepartments(): Promise<ApiResponse<Department[]>> {
    return this.request<ApiResponse<Department[]>>('/departments/');
  }

  async createDepartment(data: { dept_name: string; parent_id: number; dept_desc: string | null }): Promise<ApiResponse<Department>> {
    return this.request<ApiResponse<Department>>('/departments/', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateDepartment(dept_id: number, data: { dept_name?: string; parent_id?: number; dept_desc?: string | null }): Promise<ApiResponse<Department>> {
    return this.request<ApiResponse<Department>>(`/departments/${dept_id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deleteDepartment(dept_id: number): Promise<ApiResponse<boolean>> {
    return this.request<ApiResponse<boolean>>(`/departments/${dept_id}`, {
      method: 'DELETE',
    });
  }

  // ===== 知识档案 =====
  // ===== 知识文件夹 =====
  async listKnowledgeFolders(space: string, parentId: number | null): Promise<ApiResponse<any[]>> {
    const sp = new URLSearchParams({ space });
    if (parentId) sp.set('parent_id', String(parentId));
    return this.request(`/knowledge/folders/list?${sp}`);
  }
  async getKnowledgeFolderPath(folderId: number): Promise<ApiResponse<any[]>> {
    return this.request(`/knowledge/folders/${folderId}/path`);
  }
  async createKnowledgeFolder(data: { folder_name: string; space: string; parent_id?: number | null }): Promise<ApiResponse<any>> {
    return this.request('/knowledge/folders', { method: 'POST', body: JSON.stringify(data) });
  }
  async updateKnowledgeFolder(folderId: number, data: { folder_name?: string; parent_id?: number }): Promise<ApiResponse<any>> {
    return this.request(`/knowledge/folders/${folderId}`, { method: 'PUT', body: JSON.stringify(data) });
  }
  async deleteKnowledgeFolder(folderId: number): Promise<ApiResponse<void>> {
    return this.request(`/knowledge/folders/${folderId}`, { method: 'DELETE' });
  }
  async downloadKnowledgeFile(fileId: number): Promise<void> {
    await this.downloadBlob(`/knowledge/download/files/${fileId}`);
  }
  async downloadKnowledgeArchive(archiveId: number): Promise<void> {
    await this.downloadBlob(`/knowledge/download/archives/${archiveId}`);
  }

  async getKnowledgeArchives(params?: {
    page?: number;
    page_size?: number;
    uploader_id?: number;
    status?: number;
    space?: string;
    folder_id?: number; // 0=根目录(未分类)；不传=不过滤(全局)
  }): Promise<PaginatedResponse<KnowledgeArchive>> {
    const searchParams = new URLSearchParams();
    if (params?.page) searchParams.set('page', String(params.page));
    if (params?.page_size) searchParams.set('page_size', String(params.page_size));
    if (params?.uploader_id) searchParams.set('uploader_id', String(params.uploader_id));
    if (params?.status) searchParams.set('status', String(params.status));
    if (params?.space) searchParams.set('space', params.space);
    if (params?.folder_id !== undefined) searchParams.set('folder_id', String(params.folder_id));

    return this.request<PaginatedResponse<KnowledgeArchive>>(`/knowledge/?${searchParams}`);
  }

  async getAssignableArchives(): Promise<ApiResponse<KnowledgeArchive[]>> {
    /**
     * 获取可用于任务分配的档案列表
     *
     * 权限规则：
     * - 超级管理员/考官：部门可见 + 公开档案
     * - 教师/其他：仅部门可见档案
     * - 私有档案不显示
     */
    return this.request<ApiResponse<KnowledgeArchive[]>>('/knowledge/assignable');
  }

  async searchKnowledge(
    keyword: string,
    params?: { page?: number; page_size?: number }
  ): Promise<PaginatedResponse<KnowledgeArchive>> {
    const searchParams = new URLSearchParams();
    searchParams.set('keyword', keyword);
    if (params?.page) searchParams.set('page', String(params.page));
    if (params?.page_size) searchParams.set('page_size', String(params.page_size));

    return this.request<PaginatedResponse<KnowledgeArchive>>(`/knowledge/search?${searchParams}`);
  }

  async getKnowledgeArchive(archiveId: number): Promise<ApiResponse<KnowledgeArchive>> {
    return this.request<ApiResponse<KnowledgeArchive>>(`/knowledge/${archiveId}`);
  }

  async createKnowledgeArchive(
    data: KnowledgeArchiveCreate,
    uploaderId: number
  ): Promise<ApiResponse<KnowledgeArchive>> {
    const searchParams = new URLSearchParams();
    searchParams.set('uploader_id', String(uploaderId));

    return this.request<ApiResponse<KnowledgeArchive>>(`/knowledge/?${searchParams}`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateKnowledgeArchive(
    archiveId: number,
    data: KnowledgeArchiveUpdate
  ): Promise<ApiResponse<KnowledgeArchive>> {
    return this.request<ApiResponse<KnowledgeArchive>>(`/knowledge/${archiveId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deleteKnowledgeArchive(archiveId: number): Promise<ApiResponse<void>> {
    return this.request<ApiResponse<void>>(`/knowledge/${archiveId}`, {
      method: 'DELETE',
    });
  }

  // ===== 文件上传（分片） =====
  async initUpload(data: {
    file_name: string;
    file_size: number;
    file_type?: string;
    archive_id?: number;
  }): Promise<ApiResponse<{
    upload_id: string;
    object_name: string;
    chunk_size: number;
    total_chunks: number;
  }>> {
    return this.request<ApiResponse<{
      upload_id: string;
      object_name: string;
      chunk_size: number;
      total_chunks: number;
    }>>('/knowledge/upload/init', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async uploadChunk(
    uploadId: string,
    chunkIndex: number,
    chunk: Blob,
    maxRetries: number = 3
  ): Promise<ApiResponse<{ upload_id: string; chunk_index: number; uploaded: boolean }>> {
    const formData = new FormData();
    formData.append('upload_id', uploadId);
    formData.append('chunk_index', String(chunkIndex));
    formData.append('chunk', chunk);

    const url = `${API_BASE}/knowledge/upload/chunk`;
    const headers: HeadersInit = {};
    if (this.token) {
      headers['Authorization'] = `Bearer ${this.token}`;
    }

    // 使用 AbortController 设置超时（5分钟）
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 5 * 60 * 1000);

    let lastError: Error | null = null;

    for (let attempt = 0; attempt < maxRetries; attempt++) {
      try {
        const response = await fetch(url, {
          method: 'POST',
          headers,
          body: formData,
          signal: controller.signal,
        });

        clearTimeout(timeoutId);

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(errorData.message || `请求失败: ${response.status}`);
        }

        return response.json();
      } catch (error: any) {
        lastError = error;

        // 如果是用户主动取消，不重试
        if (error.name === 'AbortError') {
          throw new Error('上传超时，请检查网络连接后重试');
        }

        // 网络错误，等待后重试
        if (attempt < maxRetries - 1) {
          console.log(`分片 ${chunkIndex} 上传失败，第 ${attempt + 1} 次重试...`);
          await new Promise(resolve => setTimeout(resolve, 1000 * (attempt + 1)));
        }
      }
    }

    clearTimeout(timeoutId);
    throw lastError || new Error('上传失败');
  }

  async completeUpload(data: {
    upload_id: string;
    archive_id: number;
  }): Promise<ApiResponse<{
    file_id: number;
    file_name: string;
    file_path: string;
    file_size: number;
    file_type: string;
  }>> {
    return this.request<ApiResponse<{
      file_id: number;
      file_name: string;
      file_path: string;
      file_size: number;
      file_type: string;
    }>>('/knowledge/upload/complete', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async cancelUpload(uploadId: string): Promise<ApiResponse<void>> {
    return this.request<ApiResponse<void>>(`/knowledge/upload/cancel?upload_id=${uploadId}`, {
      method: 'POST',
    });
  }

  async getFilePreviewUrl(fileId: number): Promise<ApiResponse<{
    file_id: number;
    file_name: string;
    file_type: string;
    file_size: number;
    preview_url: string;
  }>> {
    return this.request<ApiResponse<{
      file_id: number;
      file_name: string;
      file_type: string;
      file_size: number;
      preview_url: string;
    }>>(`/knowledge/files/${fileId}/preview`);
  }

  async getArchiveFiles(archiveId: number): Promise<ApiResponse<{
    file_id: number;
    archive_id: number;
    file_name: string;
    file_type: string;
    file_size: number;
    file_path: string;
    upload_time: string;
    preview_url: string;
  }[]>> {
    return this.request<ApiResponse<{
      file_id: number;
      archive_id: number;
      file_name: string;
      file_type: string;
      file_size: number;
      file_path: string;
      upload_time: string;
      preview_url: string;
    }[]>>(`/knowledge/${archiveId}/files`);
  }

  async deleteFile(fileId: number): Promise<ApiResponse<void>> {
    return this.request<ApiResponse<void>>(`/knowledge/files/${fileId}`, {
      method: 'DELETE',
    });
  }

  // ===== 试卷管理 =====
  async getExamPapers(params?: {
    page?: number;
    page_size?: number;
    teacher_id?: number;
    archive_id?: number;
  }): Promise<PaginatedResponse<ExamPaper>> {
    const searchParams = new URLSearchParams();
    if (params?.page) searchParams.set('page', String(params.page));
    if (params?.page_size) searchParams.set('page_size', String(params.page_size));
    if (params?.teacher_id) searchParams.set('teacher_id', String(params.teacher_id));
    if (params?.archive_id) searchParams.set('archive_id', String(params.archive_id));

    return this.request<PaginatedResponse<ExamPaper>>(`/exams/papers?${searchParams}`);
  }

  async getExamPaper(paperId: number): Promise<ApiResponse<ExamPaperWithQuestions>> {
    return this.request<ApiResponse<ExamPaperWithQuestions>>(`/exams/papers/${paperId}`);
  }

  async createExamPaper(
    data: ExamPaperCreate,
    teacherId: number
  ): Promise<ApiResponse<ExamPaperWithQuestions>> {
    const searchParams = new URLSearchParams();
    searchParams.set('teacher_id', String(teacherId));

    return this.request<ApiResponse<ExamPaperWithQuestions>>(`/exams/papers?${searchParams}`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateExamPaper(
    paperId: number,
    data: ExamPaperUpdate
  ): Promise<ApiResponse<ExamPaper>> {
    return this.request<ApiResponse<ExamPaper>>(`/exams/papers/${paperId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deleteExamPaper(paperId: number): Promise<ApiResponse<void>> {
    return this.request<ApiResponse<void>>(`/exams/papers/${paperId}`, {
      method: 'DELETE',
    });
  }

  // ===== 题目管理 =====
  async addQuestionToPaper(
    paperId: number,
    data: QuestionCreateInPaper,
    teacherId: number
  ): Promise<ApiResponse<ExamQuestion>> {
    const searchParams = new URLSearchParams();
    searchParams.set('teacher_id', String(teacherId));

    return this.request<ApiResponse<ExamQuestion>>(
      `/exams/papers/${paperId}/questions?${searchParams}`,
      {
        method: 'POST',
        body: JSON.stringify(data),
      }
    );
  }

  async updateQuestionInPaper(
    paperId: number,
    questionId: number,
    data: QuestionUpdateInPaper,
    teacherId: number
  ): Promise<ApiResponse<ExamQuestion>> {
    const searchParams = new URLSearchParams();
    searchParams.set('teacher_id', String(teacherId));

    return this.request<ApiResponse<ExamQuestion>>(
      `/exams/papers/${paperId}/questions/${questionId}?${searchParams}`,
      {
        method: 'PUT',
        body: JSON.stringify(data),
      }
    );
  }

  async deleteQuestionFromPaper(
    paperId: number,
    questionId: number,
    teacherId: number
  ): Promise<ApiResponse<void>> {
    const searchParams = new URLSearchParams();
    searchParams.set('teacher_id', String(teacherId));

    return this.request<ApiResponse<void>>(
      `/exams/papers/${paperId}/questions/${questionId}?${searchParams}`,
      { method: 'DELETE' }
    );
  }

  async reorderQuestionsInPaper(
    paperId: number,
    questionOrders: QuestionReorderItem[],
    teacherId: number
  ): Promise<ApiResponse<void>> {
    const searchParams = new URLSearchParams();
    searchParams.set('teacher_id', String(teacherId));

    return this.request<ApiResponse<void>>(
      `/exams/papers/${paperId}/questions/reorder?${searchParams}`,
      {
        method: 'PUT',
        body: JSON.stringify(questionOrders),
      }
    );
  }

  async updatePaperWithQuestions(
    paperId: number,
    data: ExamPaperUpdateWithQuestions,
    teacherId: number
  ): Promise<ApiResponse<ExamPaperWithQuestions>> {
    const searchParams = new URLSearchParams();
    searchParams.set('teacher_id', String(teacherId));

    return this.request<ApiResponse<ExamPaperWithQuestions>>(
      `/exams/papers/${paperId}/full?${searchParams}`,
      {
        method: 'PUT',
        body: JSON.stringify(data),
      }
    );
  }

  async generateExamFromArchive(
    data: LLMExamGenerate,
    teacherId: number,
    signal?: AbortSignal
  ): Promise<ApiResponse<ExamPaperWithQuestions>> {
    const searchParams = new URLSearchParams();
    searchParams.set('teacher_id', String(teacherId));

    // AI生成可能需要较长时间，设置5分钟超时
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 5 * 60 * 1000);

    // 如果外部传入signal，监听其abort事件
    if (signal) {
      signal.addEventListener('abort', () => {
        controller.abort();
        clearTimeout(timeoutId);
      });
    }

    try {
      const response = await fetch(`${API_BASE}/exams/generate?${searchParams}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(this.token ? { 'Authorization': `Bearer ${this.token}` } : {}),
        },
        body: JSON.stringify(data),
        signal: controller.signal,
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `请求失败: ${response.status}`);
      }

      return response.json();
    } catch (error: any) {
      clearTimeout(timeoutId);
      if (error.name === 'AbortError') {
        throw new Error('生成试卷已取消');
      }
      throw error;
    }
  }

  /**
   * 本地上传题目文件 → AI 识别提取题目 → 创建试卷
   * 文件仅临时解析，不入库、不入 MinIO
   */
  async extractFromUpload(
    files: File[],
    paperName: string,
    teacherId: number,
    signal?: AbortSignal
  ): Promise<ApiResponse<ExamPaperWithQuestions>> {
    const searchParams = new URLSearchParams();
    searchParams.set('teacher_id', String(teacherId));

    const formData = new FormData();
    for (const file of files) {
      formData.append('files', file);
    }
    if (paperName) {
      formData.append('paper_name', paperName);
    }

    // AI识别可能需要较长时间，设置5分钟超时
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 5 * 60 * 1000);
    if (signal) {
      signal.addEventListener('abort', () => {
        controller.abort();
        clearTimeout(timeoutId);
      });
    }

    try {
      const response = await fetch(`${API_BASE}/exams/extract-upload?${searchParams}`, {
        method: 'POST',
        headers: this.token ? { 'Authorization': `Bearer ${this.token}` } : {},
        body: formData,
        signal: controller.signal,
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || errorData.detail || `请求失败: ${response.status}`);
      }

      return response.json();
    } catch (error: any) {
      clearTimeout(timeoutId);
      if (error.name === 'AbortError') {
        throw new Error('识别题目已取消');
      }
      throw error;
    }
  }

  // ===== 考试任务 =====
  async getExamTasks(params?: {
    page?: number;
    page_size?: number;
    teacher_id?: number;
    paper_id?: number;
  }): Promise<PaginatedResponse<ExamTask>> {
    const searchParams = new URLSearchParams();
    if (params?.page) searchParams.set('page', String(params.page));
    if (params?.page_size) searchParams.set('page_size', String(params.page_size));
    if (params?.teacher_id) searchParams.set('teacher_id', String(params.teacher_id));
    if (params?.paper_id) searchParams.set('paper_id', String(params.paper_id));

    return this.request<PaginatedResponse<ExamTask>>(`/exams/tasks?${searchParams}`);
  }

  async getExamTask(taskId: number): Promise<ApiResponse<ExamTask>> {
    return this.request<ApiResponse<ExamTask>>(`/exams/tasks/${taskId}`);
  }

  async createExamTask(
    data: ExamTaskCreate,
    teacherId: number
  ): Promise<ApiResponse<ExamTask>> {
    const searchParams = new URLSearchParams();
    searchParams.set('teacher_id', String(teacherId));

    return this.request<ApiResponse<ExamTask>>(`/exams/tasks?${searchParams}`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateExamTask(
    taskId: number,
    data: ExamTaskUpdate
  ): Promise<ApiResponse<ExamTask>> {
    return this.request<ApiResponse<ExamTask>>(`/exams/tasks/${taskId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deleteExamTask(taskId: number): Promise<ApiResponse<void>> {
    return this.request<ApiResponse<void>>(`/exams/tasks/${taskId}`, {
      method: 'DELETE',
    });
  }

  // ===== 答题 =====
  async getTaskPaper(
    taskId: number,
    studentId: number
  ): Promise<ApiResponse<ExamPaperWithQuestions>> {
    const searchParams = new URLSearchParams();
    searchParams.set('student_id', String(studentId));

    return this.request<ApiResponse<ExamPaperWithQuestions>>(
      `/exams/tasks/${taskId}/paper?${searchParams}`
    );
  }

  async submitExamAnswers(
    taskId: number,
    data: ExamSubmit,
    studentId: number
  ): Promise<ApiResponse<{ answer_count: number }>> {
    const searchParams = new URLSearchParams();
    searchParams.set('student_id', String(studentId));

    return this.request<ApiResponse<{ answer_count: number }>>(
      `/exams/tasks/${taskId}/submit?${searchParams}`,
      {
        method: 'POST',
        body: JSON.stringify(data),
      }
    );
  }

  async getExamResult(taskId: number, studentId: number): Promise<ApiResponse<ExamResult>> {
    const searchParams = new URLSearchParams();
    searchParams.set('student_id', String(studentId));

    return this.request<ApiResponse<ExamResult>>(
      `/exams/tasks/${taskId}/result?${searchParams}`
    );
  }

  // ===== 查看考试答题详情 =====
  async getExamTaskResult(
    taskId: number,
    studentId: number
  ): Promise<ApiResponse<ExamSubmitResult>> {
    const searchParams = new URLSearchParams();
    searchParams.set('student_id', String(studentId));

    return this.request<ApiResponse<ExamSubmitResult>>(
      `/exams/tasks/${taskId}/result?${searchParams}`
    );
  }

  // ===== 学习任务 =====
  async getLearningTasks(params?: {
    page?: number;
    page_size?: number;
    teacher_id?: number;
  }): Promise<PaginatedResponse<LearningTask>> {
    const searchParams = new URLSearchParams();
    if (params?.page) searchParams.set('page', String(params.page));
    if (params?.page_size) searchParams.set('page_size', String(params.page_size));
    if (params?.teacher_id) searchParams.set('teacher_id', String(params.teacher_id));

    return this.request<PaginatedResponse<LearningTask>>(`/exams/learning-tasks?${searchParams}`);
  }

  async getLearningTask(taskId: number): Promise<ApiResponse<LearningTask>> {
    return this.request<ApiResponse<LearningTask>>(`/exams/learning-tasks/${taskId}`);
  }

  async createLearningTask(
    data: LearningTaskCreate,
    teacherId: number
  ): Promise<ApiResponse<LearningTask>> {
    const searchParams = new URLSearchParams();
    searchParams.set('teacher_id', String(teacherId));

    return this.request<ApiResponse<LearningTask>>(`/exams/learning-tasks?${searchParams}`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateLearningTask(
    taskId: number,
    data: LearningTaskUpdate
  ): Promise<ApiResponse<LearningTask>> {
    return this.request<ApiResponse<LearningTask>>(`/exams/learning-tasks/${taskId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deleteLearningTask(taskId: number): Promise<ApiResponse<void>> {
    return this.request<ApiResponse<void>>(`/exams/learning-tasks/${taskId}`, {
      method: 'DELETE',
    });
  }

  // ===== 学生任务 =====
  async getMyExamTasks(params: {
    student_id: number;
    page?: number;
    page_size?: number;
  }): Promise<PaginatedResponse<StudentExamTask>> {
    const searchParams = new URLSearchParams();
    searchParams.set('student_id', String(params.student_id));
    if (params?.page) searchParams.set('page', String(params.page));
    if (params?.page_size) searchParams.set('page_size', String(params.page_size));

    return this.request<PaginatedResponse<StudentExamTask>>(`/exams/my-exam-tasks?${searchParams}`);
  }

  async getMyLearningTasks(params: {
    student_id: number;
    page?: number;
    page_size?: number;
  }): Promise<PaginatedResponse<StudentLearningTask>> {
    const searchParams = new URLSearchParams();
    searchParams.set('student_id', String(params.student_id));
    if (params?.page) searchParams.set('page', String(params.page));
    if (params?.page_size) searchParams.set('page_size', String(params.page_size));

    return this.request<PaginatedResponse<StudentLearningTask>>(`/exams/my-learning-tasks?${searchParams}`);
  }

  // ===== 学习详情 =====
  async getLearningTaskDetail(taskId: number, studentId: number): Promise<ApiResponse<LearningTaskDetail>> {
    return this.request<ApiResponse<LearningTaskDetail>>(`/exams/learning-tasks/${taskId}/detail?student_id=${studentId}`);
  }

  async updateFileLearningProgress(
    taskId: number,
    fileId: number,
    studentId: number,
    data: UpdateFileProgressRequest
  ): Promise<ApiResponse<LearningFileProgress>> {
    return this.request<ApiResponse<LearningFileProgress>>(
      `/exams/learning-tasks/${taskId}/files/${fileId}/progress?student_id=${studentId}`,
      {
        method: 'POST',
        body: JSON.stringify(data),
      }
    );
  }

  async getUserByAccount(account: string): Promise<ApiResponse<User>> {
    return this.request<ApiResponse<User>>(`/users/detail?account=${encodeURIComponent(account)}`);
  }

  async getUserPermissions(account: string): Promise<ApiResponse<string[]>> {
    return this.request<ApiResponse<string[]>>(`/users/permissions?account=${encodeURIComponent(account)}`);
  }

  // ===== 知识问答 =====
  async knowledgeChat(data: ChatRequest): Promise<ApiResponse<ChatResponse>> {
    return this.request<ApiResponse<ChatResponse>>('/knowledge/chat', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async getChatSessions(): Promise<ApiResponse<ChatSession[]>> {
    return this.request<ApiResponse<ChatSession[]>>('/knowledge/chat/sessions');
  }

  async getChatHistory(sessionId: string): Promise<ApiResponse<ChatHistory>> {
    return this.request<ApiResponse<ChatHistory>>(`/knowledge/chat/sessions/${sessionId}`);
  }

  async renameChatSession(sessionId: string, title: string): Promise<ApiResponse<void>> {
    return this.request<ApiResponse<void>>(`/knowledge/chat/sessions/${sessionId}?title=${encodeURIComponent(title)}`, {
      method: 'PATCH',
    });
  }

  async deleteChatSession(sessionId: string): Promise<ApiResponse<void>> {
    return this.request<ApiResponse<void>>(`/knowledge/chat/sessions/${sessionId}`, {
      method: 'DELETE',
    });
  }

  // ===== 知识处理 =====
  async processKnowledge(archiveId: number): Promise<ApiResponse<ProcessStatus>> {
    return this.request<ApiResponse<ProcessStatus>>(`/knowledge/${archiveId}/process`, {
      method: 'POST',
    });
  }

  async getProcessStatus(archiveId: number): Promise<ApiResponse<ProcessStatus>> {
    return this.request<ApiResponse<ProcessStatus>>(`/knowledge/${archiveId}/status`);
  }

  // ===== 健康检查 =====
  async healthCheck(): Promise<{ status: string }> {
    return this.request<{ status: string }>('/health');
  }

  // ===== 角色管理 =====
  async getRoles(): Promise<ApiResponse<RoleOut[]>> {
    return this.request<ApiResponse<RoleOut[]>>('/roles/');
  }

  async getRole(roleId: number): Promise<ApiResponse<RoleOut>> {
    return this.request<ApiResponse<RoleOut>>(`/roles/${roleId}`);
  }

  async createRole(data: RoleCreate): Promise<ApiResponse<RoleOut>> {
    return this.request<ApiResponse<RoleOut>>('/roles/', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateRole(roleId: number, data: RoleUpdate): Promise<ApiResponse<RoleOut>> {
    return this.request<ApiResponse<RoleOut>>(`/roles/${roleId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deleteRole(roleId: number): Promise<ApiResponse<void>> {
    return this.request<ApiResponse<void>>(`/roles/${roleId}`, {
      method: 'DELETE',
    });
  }

  async getAllPermissions(): Promise<ApiResponse<Permission[]>> {
    return this.request<ApiResponse<Permission[]>>('/roles/permissions/all');
  }

  // ===== 标签管理 =====
  async getTags(params?: {
    page?: number;
    page_size?: number;
  }): Promise<PaginatedResponse<Tag>> {
    const searchParams = new URLSearchParams();
    if (params?.page) searchParams.set('page', String(params.page));
    if (params?.page_size) searchParams.set('page_size', String(params.page_size));
    return this.request<PaginatedResponse<Tag>>(`/tags/?${searchParams}`);
  }

  async getAllTags(): Promise<ApiResponse<Tag[]>> {
    return this.request<ApiResponse<Tag[]>>('/tags/all');
  }

  async getTag(tagId: number): Promise<ApiResponse<Tag>> {
    return this.request<ApiResponse<Tag>>(`/tags/${tagId}`);
  }

  async createTag(data: TagCreate): Promise<ApiResponse<Tag>> {
    return this.request<ApiResponse<Tag>>('/tags/', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateTag(tagId: number, data: TagUpdate): Promise<ApiResponse<Tag>> {
    return this.request<ApiResponse<Tag>>(`/tags/${tagId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deleteTag(tagId: number): Promise<ApiResponse<void>> {
    return this.request<ApiResponse<void>>(`/tags/${tagId}`, {
      method: 'DELETE',
    });
  }

  // ===== 任务分配 =====
  async assignLearningTask(
    taskId: number,
    studentIds: number[]
  ): Promise<ApiResponse<{ assigned_count: number }>> {
    return this.request<ApiResponse<{ assigned_count: number }>>(
      `/exams/learning-tasks/${taskId}/assign`,
      {
        method: 'POST',
        body: JSON.stringify({ student_ids: studentIds }),
      }
    );
  }

  async getLearningTaskStudents(taskId: number): Promise<ApiResponse<AssignedStudent[]>> {
    return this.request<ApiResponse<AssignedStudent[]>>(
      `/exams/learning-tasks/${taskId}/students`
    );
  }

  async getLearningTaskFiles(taskId: number): Promise<ApiResponse<any[]>> {
    return this.request<ApiResponse<any[]>>(
      `/exams/learning-tasks/${taskId}/files`
    );
  }

  async updateFileRequiredDuration(
    taskId: number,
    fileId: number,
    requiredDuration: number,
    teacherId: number
  ): Promise<ApiResponse<{ task_id: number; file_id: number; required_duration: number; updated_count: number }>> {
    return this.request<ApiResponse<{ task_id: number; file_id: number; required_duration: number; updated_count: number }>>(
      `/exams/learning-tasks/${taskId}/files/${fileId}/required-duration?teacher_id=${teacherId}`,
      {
        method: 'PUT',
        body: JSON.stringify({ required_duration: requiredDuration }),
      }
    );
  }

  async getLearningTaskStudentsCompletion(taskId: number): Promise<ApiResponse<TaskStudentsCompletion>> {
    return this.request<ApiResponse<TaskStudentsCompletion>>(
      `/exams/learning-tasks/${taskId}/students/completion`
    );
  }

  async assignExamTask(
    taskId: number,
    studentIds: number[]
  ): Promise<ApiResponse<{ assigned_count: number }>> {
    return this.request<ApiResponse<{ assigned_count: number }>>(
      `/exams/tasks/${taskId}/assign`,
      {
        method: 'POST',
        body: JSON.stringify({ student_ids: studentIds }),
      }
    );
  }

  async getExamTaskStudents(taskId: number): Promise<ApiResponse<AssignedStudent[]>> {
    return this.request<ApiResponse<AssignedStudent[]>>(
      `/exams/tasks/${taskId}/students`
    );
  }

  async getExamTaskStudentsCompletion(taskId: number): Promise<ApiResponse<TaskStudentsCompletion>> {
    return this.request<ApiResponse<TaskStudentsCompletion>>(
      `/exams/tasks/${taskId}/students/completion`
    );
  }

  // ===== 仪表盘统计 =====
  async getDashboardStats(): Promise<ApiResponse<Record<string, number>>> {
    return this.request<ApiResponse<Record<string, number>>>('/dashboard/stats');
  }

  // ===== 操作日志 =====
  async getOperationLogs(params?: {
    page?: number;
    page_size?: number;
    user_id?: number;
    operation_type?: string;
    resource_type?: string;
  }): Promise<PaginatedResponse<OperationLogOut>> {
    const searchParams = new URLSearchParams();
    if (params?.page !== undefined) searchParams.set('page', params.page.toString());
    if (params?.page_size !== undefined) searchParams.set('page_size', params.page_size.toString());
    if (params?.user_id !== undefined) searchParams.set('user_id', params.user_id.toString());
    if (params?.operation_type !== undefined) searchParams.set('operation_type', params.operation_type);
    if (params?.resource_type !== undefined) searchParams.set('resource_type', params.resource_type);

    return this.request<PaginatedResponse<OperationLogOut>>(
      `/operation-logs/${searchParams.toString() ? '?' + searchParams.toString() : ''}`
    );
  }

  async getOperationLog(logId: number): Promise<ApiResponse<OperationLogOut>> {
    return this.request<ApiResponse<OperationLogOut>>(`/operation-logs/${logId}`);
  }

  // ===== 拜访记录 =====

  async extractVisitInfo(data: VisitExtractRequest): Promise<ApiResponse<VisitInfoExtracted>> {
    return this.request<ApiResponse<VisitInfoExtracted>>('/visit-records/extract', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async createVisitRecord(data: VisitRecordCreate): Promise<ApiResponse<VisitRecord>> {
    return this.request<ApiResponse<VisitRecord>>('/visit-records/', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async getVisitRecords(params?: {
    page?: number;
    page_size?: number;
  }): Promise<PaginatedResponse<VisitRecord>> {
    const searchParams = new URLSearchParams();
    if (params?.page) searchParams.set('page', String(params.page));
    if (params?.page_size) searchParams.set('page_size', String(params.page_size));
    return this.request<PaginatedResponse<VisitRecord>>(
      `/visit-records/${searchParams.toString() ? '?' + searchParams.toString() : ''}`
    );
  }

  async deleteVisitRecord(visitId: number): Promise<ApiResponse<void>> {
    return this.request<ApiResponse<void>>(`/visit-records/${visitId}`, {
      method: 'DELETE',
    });
  }

  async getVisitRecord(visitId: number): Promise<ApiResponse<VisitRecord>> {
    return this.request<ApiResponse<VisitRecord>>(`/visit-records/${visitId}`);
  }

  async transcribeAudio(audioFile: File): Promise<ApiResponse<{ text: string }>> {
    const formData = new FormData();
    formData.append('file', audioFile);
    const url = `${API_BASE}/visit-records/transcribe`;
    const headers: Record<string, string> = {};
    if (this.token) headers['Authorization'] = `Bearer ${this.token}`;
    const response = await fetch(url, { method: 'POST', body: formData, headers });
    if (!response.ok) {
      const err = await response.json().catch(() => ({ message: '请求失败' }));
      throw new Error(err.detail || err.message || '请求失败');
    }
    return response.json();
  }

  async updateVisitRecord(visitId: number, data: VisitRecordCreate): Promise<ApiResponse<VisitRecord>> {
    return this.request<ApiResponse<VisitRecord>>(`/visit-records/${visitId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async toggleArchive(visitId: number): Promise<ApiResponse<{ is_archived: boolean }>> {
    return this.request(`/visit-records/${visitId}/archive`, { method: 'POST' });
  }

  async exportVisitReport(visitId: number): Promise<void> {
    const resp = await fetch(`${API_BASE}/visit-records/${visitId}/export`, {
      headers: this.token ? { Authorization: `Bearer ${this.token}` } : {},
    });
    if (!resp.ok) {
      const err = await resp.json().catch(() => ({ detail: '导出失败' }));
      throw new Error(err.detail || `导出失败 (${resp.status})`);
    }
    // 从 Content-Disposition 头解析文件名
    const disposition = resp.headers.get('Content-Disposition') || '';
    const match = disposition.match(/filename\*?=(?:UTF-8''|"?) ?([^;"]+)/);
    const filename = match ? decodeURIComponent(match[1]) : `出差报告_${visitId}.docx`;
    const blob = await resp.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }

  async exportVisitReportsBatch(visitIds: number[]): Promise<void> {
    // 批量导出工作记录报告：后端打包多份 Word 为 ZIP
    const resp = await fetch(`${API_BASE}/visit-records/export-batch`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(this.token ? { Authorization: `Bearer ${this.token}` } : {}),
      },
      body: JSON.stringify({ visit_ids: visitIds }),
    });
    if (!resp.ok) {
      const err = await resp.json().catch(() => ({ detail: '批量导出失败' }));
      throw new Error(err.detail || `批量导出失败 (${resp.status})`);
    }
    const disposition = resp.headers.get('Content-Disposition') || '';
    const match = disposition.match(/filename\*?=(?:UTF-8''|"?) ?([^;"]+)/);
    const filename = match ? decodeURIComponent(match[1]) : `工作记录出差报告_${visitIds.length}份.zip`;
    const blob = await resp.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }

  // ===== 内容推介 — 视频粒度 =====

  async uploadPromotionFile(file: File, data: PromotionFileCreate): Promise<ApiResponse<PromotionFile>> {
    const fd = new FormData(); fd.append('file', file);
    Object.entries(data).forEach(([k,v]) => { if (v !== undefined) fd.append(k, String(v)); });
    const url = `${API_BASE}/promotions/files/upload`;
    const headers: Record<string, string> = {};
    if (this.token) headers['Authorization'] = `Bearer ${this.token}`;
    const res = await fetch(url, { method: 'POST', body: fd, headers });
    if (!res.ok) throw new Error((await res.json().catch(() => ({ detail: '上传失败' }))).detail || '上传失败');
    return res.json();
  }
  async getPromotionFiles(params?: { page?: number; page_size?: number }): Promise<PaginatedResponse<PromotionFile>> {
    const sp = new URLSearchParams();
    if (params?.page) sp.set('page', String(params.page));
    if (params?.page_size) sp.set('page_size', String(params.page_size));
    return this.request(`/promotions/files?${sp}`);
  }
  async updatePromotionFile(id: number, data: PromotionFileUpdate): Promise<ApiResponse<PromotionFile>> {
    return this.request(`/promotions/files/${id}`, { method: 'PUT', body: JSON.stringify(data) });
  }
  async deletePromotionFile(id: number): Promise<ApiResponse<void>> {
    return this.request(`/promotions/files/${id}`, { method: 'DELETE' });
  }
  async getPromotionViewLogs(fileId: number): Promise<ApiResponse<PromotionViewLog[]>> {
    return this.request(`/promotions/files/${fileId}/view-logs`);
  }
  async getInviteeFiles(): Promise<ApiResponse<PromotionFile[]>> {
    return this.request('/promotions/invitee/files');
  }
  async recordViewLog(data: { file_id: number; view_start: string; view_duration: number }): Promise<ApiResponse<void>> {
    return this.request('/promotions/invitee/files/view-log', { method: 'POST', body: JSON.stringify(data) });
  }

  // ===== 质量事故 =====

  async createQualityIncident(data: { incident_name?: string; archive_id?: number }): Promise<ApiResponse<{ incident_id: number; incident_no: string }>> {
    return this.request('/quality-incidents/', { method: 'POST', body: JSON.stringify(data) });
  }

  async getQualityIncidents(params?: { page?: number; page_size?: number; incident_type?: string; severity_level?: string; keyword?: string; start_time?: string; end_time?: string }): Promise<ApiResponse<{ items: QualityIncidentListItem[]; total: number; page: number; page_size: number; total_pages: number }>> {
    const qs = new URLSearchParams();
    if (params) Object.entries(params).forEach(([k, v]) => { if (v !== undefined && v !== '') qs.append(k, String(v)); });
    return this.request(`/quality-incidents/?${qs.toString()}`);
  }

  async getQualityIncidentDetail(incidentId: number): Promise<ApiResponse<QualityIncidentDetail>> {
    return this.request(`/quality-incidents/${incidentId}`);
  }

  async updateQualityIncident(incidentId: number, data: Record<string, any>): Promise<ApiResponse<any>> {
    return this.request(`/quality-incidents/${incidentId}`, { method: 'PUT', body: JSON.stringify(data) });
  }

  async deleteQualityIncident(incidentId: number): Promise<ApiResponse<void>> {
    return this.request(`/quality-incidents/${incidentId}`, { method: 'DELETE' });
  }

  async extractQualityIncident(incidentId: number): Promise<ApiResponse<{ incident_id: number; status: number; file_count: number; merged_fields_count: number; conflicts_count: number }>> {
    return this.request(`/quality-incidents/${incidentId}/extract`, { method: 'POST' });
  }

  async getExtractionProgress(incidentId: number): Promise<ApiResponse<{ incident_id: number; extraction_status: number; extraction_error: string | null; total_files: number; completed_files: number; failed_files: number; processing_files: number; current_file: string | null; message: string }>> {
    return this.request(`/quality-incidents/${incidentId}/progress`);
  }

  async retryExtractQualityIncident(incidentId: number): Promise<ApiResponse<any>> {
    return this.request(`/quality-incidents/${incidentId}/extract/retry`, { method: 'POST' });
  }

  async getExtractionResults(incidentId: number): Promise<ApiResponse<{ incident_id: number; merge_result?: ExtractionResult | null; file_results: ExtractionResult[] }>> {
    return this.request(`/quality-incidents/${incidentId}/extractions`);
  }

  async repairIncidentTables(incidentId: number): Promise<ApiResponse<{ incident_id: number; fields_written: number }>> {
    return this.request(`/quality-incidents/${incidentId}/repair`, { method: 'POST' });
  }

  async confirmExtraction(data: ExtractionConfirmRequest): Promise<ApiResponse<any>> {
    return this.request(`/quality-incidents/${data.incident_id}/confirm`, { method: 'POST', body: JSON.stringify(data) });
  }

  async uploadQualityIncidentFile(incidentId: number, file: File): Promise<ApiResponse<{ file_id: number; file_name: string; file_size: number }>> {
    const formData = new FormData();
    formData.append('file', file);
    return this.request(`/quality-incidents/${incidentId}/files/upload`, { method: 'POST', body: formData });
  }

  async deleteQualityIncidentFile(fileId: number): Promise<ApiResponse<void>> {
    return this.request(`/quality-incidents/files/${fileId}`, { method: 'DELETE' });
  }

  async qualityChat(data: { question: string; incident_ids?: number[] }): Promise<ApiResponse<{ answer: string; matched_incidents: QualityIncidentListItem[]; query_conditions?: Record<string, any> | null }>> {
    return this.request('/quality-incidents/chat', { method: 'POST', body: JSON.stringify(data) });
  }

  // ==================== 图纸管理 ====================

  // ---- 部件类型（系统预设，只读） ----
  async getDrawingComponentTypes(): Promise<ApiResponse<DrawingComponentType[]>> {
    return this.request('/drawings/component-types');
  }

  // ---- AI 参数提取（识别候选，不入库；确认后随素材创建/更新落库） ----
  async extractDrawingParams(file: File): Promise<ApiResponse<{ material_name: string; params: DrawingParamItem[] }>> {
    const fd = new FormData();
    fd.append('file', file);
    return this.request('/drawings/extract-params', { method: 'POST', body: fd });
  }

  // ---- 素材 ----
  async getDrawingMaterials(params: { component_type_id: number; keyword?: string; page?: number; page_size?: number }): Promise<PaginatedResponse<DrawingMaterialListItem>> {
    const sp = new URLSearchParams();
    sp.set('component_type_id', String(params.component_type_id));
    if (params.keyword) sp.set('keyword', params.keyword);
    if (params.page) sp.set('page', String(params.page));
    if (params.page_size) sp.set('page_size', String(params.page_size));
    return this.request(`/drawings/materials?${sp}`);
  }
  async getDrawingMaterial(id: number): Promise<ApiResponse<DrawingMaterial>> {
    return this.request(`/drawings/materials/${id}`);
  }
  async createDrawingMaterial(data: { component_type_id: number; material_name: string; specification?: string; params: DrawingParamItem[]; params_source?: 'manual' | 'ai'; remark?: string }): Promise<ApiResponse<DrawingMaterial>> {
    return this.request('/drawings/materials', { method: 'POST', body: JSON.stringify(data) });
  }
  async updateDrawingMaterial(id: number, data: { material_name?: string; specification?: string; params?: DrawingParamItem[]; params_source?: 'manual' | 'ai'; remark?: string }): Promise<ApiResponse<DrawingMaterial>> {
    return this.request(`/drawings/materials/${id}`, { method: 'PUT', body: JSON.stringify(data) });
  }
  async deleteDrawingMaterial(id: number): Promise<ApiResponse<void>> {
    return this.request(`/drawings/materials/${id}`, { method: 'DELETE' });
  }
  async getDrawingMaterialDrawings(materialId: number): Promise<ApiResponse<DrawingFileItem[]>> {
    return this.request(`/drawings/materials/${materialId}/drawings`);
  }

  // ---- Excel 导入 ----

  // ---- 图纸上传（分片） ----
  async initDrawingUpload(data: { scope: 'material' | 'product'; material_id?: number; product_id?: number; file_name: string; file_size: number }): Promise<ApiResponse<{ upload_id: string; object_name: string; chunk_size: number; total_chunks: number }>> {
    return this.request('/drawings/upload/init', { method: 'POST', body: JSON.stringify(data) });
  }
  async uploadDrawingChunk(uploadId: string, chunkIndex: number, chunk: Blob): Promise<ApiResponse<any>> {
    const fd = new FormData();
    fd.append('upload_id', uploadId);
    fd.append('chunk_index', String(chunkIndex));
    fd.append('chunk', chunk);
    return this.request('/drawings/upload/chunk', { method: 'POST', body: fd });
  }
  async completeDrawingUpload(data: { upload_id: string; drawing_no?: string; version_note?: string }): Promise<ApiResponse<DrawingFileItem>> {
    return this.request('/drawings/upload/complete', { method: 'POST', body: JSON.stringify(data) });
  }
  async cancelDrawingUpload(uploadId: string): Promise<ApiResponse<void>> {
    return this.request('/drawings/upload/cancel', { method: 'POST', body: JSON.stringify({ upload_id: uploadId }) });
  }

  // ---- 单文件图纸上传（含分片与进度，供上传器与暂存队列复用） ----
  async uploadDrawingFile(file: File, opts: { scope: 'material' | 'product'; material_id?: number; product_id?: number }, onProgress?: (pct: number) => void): Promise<DrawingFileItem> {
    const initRes = await this.initDrawingUpload({
      scope: opts.scope, material_id: opts.material_id, product_id: opts.product_id,
      file_name: file.name, file_size: file.size,
    });
    if (!initRes.data) throw new Error('初始化上传失败');
    const { upload_id, total_chunks, chunk_size } = initRes.data;
    for (let i = 0; i < total_chunks; i++) {
      const chunk = file.slice(i * chunk_size, Math.min((i + 1) * chunk_size, file.size));
      await this.uploadDrawingChunk(upload_id, i, chunk);
      onProgress?.(Math.round(((i + 1) / total_chunks) * 100));
    }
    const completeRes = await this.completeDrawingUpload({ upload_id });
    if (!completeRes.data) throw new Error('上传完成失败');
    return completeRes.data;
  }

  // ---- 图纸检索/删除/改关联 ----
  async getDrawingFiles(params: { scope?: string; material_id?: number; product_id?: number; keyword?: string; file_ext?: string; page?: number; page_size?: number }): Promise<PaginatedResponse<DrawingFileItem>> {
    const sp = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => { if (v !== undefined && v !== null && v !== '') sp.set(k, String(v)); });
    return this.request(`/drawings/files?${sp}`);
  }
  async deleteDrawingFile(id: number): Promise<ApiResponse<void>> {
    return this.request(`/drawings/files/${id}`, { method: 'DELETE' });
  }
  async linkDrawingFile(id: number, target_scope: 'material' | 'product', target_id: number): Promise<ApiResponse<DrawingFileItem>> {
    return this.request(`/drawings/files/${id}/link`, { method: 'PUT', body: JSON.stringify({ target_scope, target_id }) });
  }

  // ---- 产品组合 ----
  async getDrawingProducts(params?: { keyword?: string; page?: number; page_size?: number }): Promise<PaginatedResponse<DrawingProductListItem>> {
    const sp = new URLSearchParams();
    Object.entries(params || {}).forEach(([k, v]) => { if (v !== undefined && v !== null && v !== '') sp.set(k, String(v)); });
    return this.request(`/drawings/products?${sp}`);
  }
  async getDrawingProduct(id: number): Promise<ApiResponse<DrawingProduct>> {
    return this.request(`/drawings/products/${id}`);
  }
  async createDrawingProduct(data: { product_name: string; remark?: string; items: Array<{ component_type_id: number; material_id: number }> }): Promise<ApiResponse<DrawingProduct>> {
    return this.request('/drawings/products', { method: 'POST', body: JSON.stringify(data) });
  }
  async updateDrawingProduct(id: number, data: { product_name?: string; remark?: string; items?: Array<{ component_type_id: number; material_id: number }> }): Promise<ApiResponse<DrawingProduct>> {
    return this.request(`/drawings/products/${id}`, { method: 'PUT', body: JSON.stringify(data) });
  }
  async deleteDrawingProduct(id: number): Promise<ApiResponse<void>> {
    return this.request(`/drawings/products/${id}`, { method: 'DELETE' });
  }
  async previewDrawingCombination(materialIds: number[]): Promise<ApiResponse<{ blocks: DrawingPreviewBlock[] }>> {
    return this.request(`/drawings/products/preview?material_ids=${materialIds.join(',')}`);
  }

  // ---- 产品文档 ----
  async generateDrawingDocuments(productId: number, docTypes: Array<'param_card' | 'manual'>): Promise<ApiResponse<DrawingDoc[]>> {
    return this.request(`/drawings/products/${productId}/documents/generate`, { method: 'POST', body: JSON.stringify({ doc_types: docTypes }) });
  }
  async uploadDrawingDocument(productId: number, docType: 'param_card' | 'manual', file: File): Promise<ApiResponse<DrawingDoc>> {
    const fd = new FormData();
    fd.append('doc_type', docType);
    fd.append('file', file);
    return this.request(`/drawings/products/${productId}/documents/upload`, { method: 'POST', body: fd });
  }
  async getDrawingProductDocuments(productId: number, onlyCurrent?: boolean): Promise<ApiResponse<DrawingDoc[]>> {
    return this.request(`/drawings/products/${productId}/documents?only_current=${onlyCurrent ? 'true' : 'false'}`);
  }
  async regenerateDrawingDocument(docId: number): Promise<ApiResponse<DrawingDoc>> {
    return this.request(`/drawings/documents/${docId}/regenerate`, { method: 'POST' });
  }
  async downloadDrawingDocument(docId: number): Promise<void> {
    await this.downloadBlob(`/drawings/documents/${docId}/download`);
  }

  // ---- 文档模板 ----
  async getDrawingDocTemplates(): Promise<ApiResponse<DrawingDocTemplate[]>> {
    return this.request('/drawings/doc-templates');
  }
  async uploadDrawingDocTemplate(docType: 'param_card' | 'manual', file: File): Promise<ApiResponse<DrawingDocTemplate>> {
    const fd = new FormData();
    fd.append('doc_type', docType);
    fd.append('file', file);
    return this.request('/drawings/doc-templates/upload', { method: 'POST', body: fd });
  }
  async downloadCurrentDrawingTemplate(docType: 'param_card' | 'manual'): Promise<void> {
    await this.downloadBlob(`/drawings/doc-templates/current?doc_type=${docType}`);
  }

  // ---- 通用 blob 下载（带鉴权，触发浏览器保存） ----
  async downloadBlob(endpoint: string): Promise<void> {
    const resp = await fetch(`${API_BASE}${endpoint}`, {
      headers: this.token ? { Authorization: `Bearer ${this.token}` } : {},
    });
    if (!resp.ok) {
      const err = await resp.json().catch(() => ({ detail: '下载失败' }));
      throw new Error(err.detail || `下载失败 (${resp.status})`);
    }
    const disposition = resp.headers.get('Content-Disposition') || '';
    const match = disposition.match(/filename\*?=(?:UTF-8''|"?) ?([^;"]+)/);
    const filename = match ? decodeURIComponent(match[1]) : 'download';
    const blob = await resp.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }
}

// 导出单例
export const api = new ApiService();

// React Hook
import { useState, useCallback } from 'react';

export function useApi<T, P extends unknown[]>(
  apiFn: (...args: P) => Promise<T>
) {
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const execute = useCallback(async (...args: P) => {
    setLoading(true);
    setError(null);

    try {
      const result = await apiFn(...args);
      setData(result);
      return result;
    } catch (err) {
      const message = err instanceof Error ? err.message : '请求失败';
      setError(message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [apiFn]);

  return { data, loading, error, execute };
}